import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BasketItemComponentDef } from '@df/basket/view/basket-item.component.def';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ImageModule } from '@df/module-image/image.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { EAttrSet, Product } from '../../catalog/product/product.decorator';
import { SPECIFICATION_ATTRIBUTES_CODES } from '../../catalog/product/product.service';
import { ProductStockDescriptionService } from '../../catalog/product/stock-levels/product-stock-description.service';
import { ProductWishlistToggleComponent } from '../../catalog/product/wishlist-toggle/product-wishlist-toggle.component';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { BasketItemProductSpecificationComponent } from '../item-product-specification/basket-item-product-specification.component';
import { BasketItemQtyComponent } from '../item-qty/basket-item-qty.component';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketItem {
            /**
             * True if product is added as a free gift
             */
            is_free_gift?: boolean;
            master_delivery_method_bs?: string | undefined;
            delivery_lead_time?: Date;
            use_additional_delivery_method?: 1 | 2;
        }
    }
}
@Component({
    selector: 'basket-view-item',
    templateUrl: './basket-view-item.html',
    styleUrls: ['basket-view-item.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ResultComponent,
        RouterLink,
        ImageModule,
        ICmsClientModule,
        TranslationModule,
        ActionComponent,
        CommonModule,
        OptionLabelPipe,
        BasketItemQtyComponent,
        BasketItemProductSpecificationComponent,
        PriceComponent,
        ProductWishlistToggleComponent,
        SizeClassDirective
    ]
})
export class BasketViewItemComponent extends BasketItemComponentDef {
    /**
     * Hides qty selector and remove button for item.
     */
    @Input()
    lock?: boolean;

    override product?: Product<IProductDataV3>;
    private translationService = inject(TranslationService);

    productStockDescriptionService = inject(ProductStockDescriptionService);

    specificationAttributesCodes = SPECIFICATION_ATTRIBUTES_CODES;
    /**
     * Stock warning
     */
    get warning(): string | undefined {
        if (!this.isSalable) {
            return this.maxQty
                ? this.translationService.transform('Only % availble', this.maxQty)
                : this.translationService.transform('Out of stock');
        }
    }

    get isFreeGift(): boolean {
        return !!this.item?.data.is_free_gift;
    }

    /**
     * If item can be edited
     */
    get isEditable(): boolean {
        return !this.item?.data.is_free_gift && !this.lock;
    }
    get isProductProtectionPlan(): boolean {
        return !!this.product?.data?.isProductProtectionPlan;
    }

    get stockDescription(): string | undefined {
        return this.product && this.productStockDescriptionService.get(0, this.product.id);
    }

    get isGardenAssembly(): boolean {
        return this.product?.data?.attribute_set === EAttrSet.assemblyPlan;
    }
    asString(value: number | string): string {
        return value as string;
    }
}
